import React, { useEffect, useRef, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import ScrollToTopButton from '../../components/TopButton/topbutton';
import instantsearch from 'instantsearch.js/es';
import { configure, hits } from 'instantsearch.js/es/widgets';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { searchConfig,typesenseServerConfig } from '../../services/typesense-server-config';
import Hit from '../Results/Hit/Hit';
import { useDispatch } from 'react-redux';
import { homeActions } from '../../store/store';
import { Helmet } from 'react-helmet-async';

const useTypesenseSearch = (query, hitsContainerId) => {
  const [hitsData, setHitsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      event_label: 'Benchmark Page',
      page: 'Benchmark Page',
      path: window.location.pathname,
    });
  }, []);

  useEffect(() => {
    dispatch(homeActions.setCurrentPage('benchmark'));
  }, [dispatch]);

  useEffect(() => {
    if (!query || !hitsContainerId) return;

    const INDEX_NAME = import.meta.env.VITE_TYPESENSE_COLLECTION_NAME;
    let token = 'noToken';


    // Create a copy of searchConfig and add the userToken to its additionalHeaders
    const TYPESENSE_SERVER_CONFIG = {
      ...searchConfig,
      additionalHeaders: {
        ...searchConfig.additionalHeaders,
        userToken: token
      }
    };



    const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
      server: TYPESENSE_SERVER_CONFIG,
      additionalSearchParameters: {
        query_by: 'decision_text,summary_vec',
        infix: 'off',
        exclude_fields: 'summary_vec',
      },
    });

    const searchClient = typesenseInstantsearchAdapter.searchClient;

    const instance = instantsearch({
      searchClient,
      indexName: INDEX_NAME,
      searchFunction(helper) {
        helper.setQuery(query).search();
      },
    });

    instance.addWidgets([
      hits({
        container: `#${hitsContainerId}`,
        cssClasses: {
          list: 'list-unstyled grid-container',
          item: 'search-result-card',
        },
        templates: {
          item(hit) {
            return `<div>${hit.html}</div>`;
          },
          empty: `<div className="pl-empty">No decisions found for <q>{{ query }}</q>. Try another search term.</div>`,
        },
        transformItems(items) {
          setHitsData(items);
          setLoading(false);
          return items;
        },
      }),
      configure({ hitsPerPage: 3 }),
    ]);

    instance.start();
    setLoading(true);

    return () => {
      instance.dispose();
    };
  }, [query, hitsContainerId]);

  return { hitsData, loading };
};

const Benchmark = () => {
  const textRefs = useRef([]);
  const [query, setQuery] = useState('');
  const [hitsContainerId, setHitsContainerId] = useState('');
  const [currentOpenDetails, setCurrentOpenDetails] = useState(null);
  const [expanded, setExpanded] = useState({});

  const handleExpandToggle = (index) => {
    setExpanded((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const setupTextContainers = () => {
    textRefs.current.forEach((textContainer) => {
      if (!textContainer) return;

      const computedHeight = textContainer.scrollHeight;
      if (computedHeight <= 65) {
        textContainer.classList.add('short-text');
      } else {
        textContainer.classList.add('long-text');
      }

      const toggleExpand = () => {
        textContainer.classList.toggle('expanded');
      };

      const toggleButton = textContainer
        .closest('li')
        .querySelector('.read-more-toggle');
      if (toggleButton) toggleButton.addEventListener('click', toggleExpand);
      textContainer.addEventListener('click', toggleExpand);
    });
  };

  const handleDetailsClick = (queryText, index, detailsElement) => {
    if (currentOpenDetails && currentOpenDetails !== detailsElement) {
      currentOpenDetails.removeAttribute('open');
    }

    setCurrentOpenDetails(detailsElement);
    setQuery(queryText);
    setHitsContainerId(`hits${index + 1}`);
  };

  const sampleQueries = [
    {
      query: 'What kind of actions lead to contempt of court?',
      decision: 'sc-contempt-no-03/2020',
      context:
        "Article 105(3) of the Sri Lankan Constitution grants the Supreme Court and the Court of Appeal the authority to punish individuals for contempt, whether the contempt is directed at themselves or at any other court, tribunal, or similar institution. This provision is rooted in the principle that the judiciary, as one of the three fundamental pillars on which the functioning of the state depends, operates independently to interpret and apply the law, ensuring justice for all. Citizens, therefore, are expected to show due respect to the system of justice in the country. It is for this reason that Article 105(3) grants the courts broad powers to punish acts of contempt. The case at hand involves a statement made by the Respondent, an attorney-at-law who had previously served in the Sri Lankan Army and suffered injuries during the civil war. The statement was made at a media briefing and was in reference to the imprisonment of Duminda Silva. The Respondent claimed that Silva’s imprisonment was a violation of human rights and went so far as to assert that the entire Supreme Court and the superior court system responsible for Silva’s conviction should themselves be imprisoned for this perceived injustice. The law of contempt of court does not exist to glorify the judiciary, but rather to protect the public’s trust in the judicial system. Disrespect toward the courts can undermine the rule of law, which could lead to an obstruction of justice. Because the judiciary is entrusted with a portion of the people's sovereignty by the Constitution, any contempt against judges is viewed as an affront to the authority of the people and their Constitution. In this case, the court held that the Respondent’s statement was made with the deliberate intent to challenge and overturn the court’s judgment in the Duminda Silva case. As a result, the Respondent was sentenced to four years of rigorous imprisonment and fined Rs. 300,000.",
    },
    {
      query: 'Can new parties to a case intervene at the appeal stage?',
      decision: 'cp-hcca-kandy-07/2019-rev',
      context:
        'Under Section 18 of the Civil Procedure Code, a court may add a party, either on application by an existing party before the hearing or on its own at any time, to ensure the complete adjudication or settlement of all matters involved. However, the court has held that allowing the intervention of a new party at the appeal stage in a direct appeal contradicts the provisions of the CPC and prejudices the rights of the petitioner. Such intervention is not an inherent power of a Civil Appellate High Court but is limited to the original court (court of first instance). While the CPC does not explicitly ban adding parties at the appeal stage, courts have ruled that this must occur before the final judgment, and the CPC separately outlines instances when new parties may be added.',
    },
    {
      query:
        'Can someone accused of trafficking heroin be released on bail pending the conclusion of the trial?',
      decision: 'sc-appeal-no-53/2022',
      context:
        "The accused-respondent was ordered by the Magistrate's Court (MC) of Maligakanda to be remanded in custody and was later indicted and charged by the High Court (HC) of Colombo for possessing and trafficking heroin, offenses punishable under the Poisons, Opium, and Dangerous Drugs Ordinance. The High Court denied bail, as the Ordinance requires exceptional circumstances to be shown for bail to be considered. The Court of Appeal (CA) subsequently overturned the High Court's decision, granting bail to the accused-respondent. The terms of the bail included the payment of case bail, surety bail, the surrender of the accused's passport, and a requirement to submit a weekly report to the Police Narcotics Bureau. The Attorney General (AG) challenged the Court of Appeal's decision before the Supreme Court (SC). The Court of Appeal had justified its decision on two grounds: first, that the chief investigating officer in the case was himself a suspect in another case, raising moral concerns; and second, the COVID-19 pandemic, which was likely to delay the trial, constituted an exceptional circumstance justifying the grant of bail. However, the Supreme Court held that the Court of Appeal’s reliance on 'morality' as an exceptional ground for granting bail had no legal basis under the Ordinance. The Court further stated that the credibility of the chief investigating officer as a witness is a matter of fact, not law, and is within the purview of the trial judge, not the appellate judge. If the trial judge finds no issue with the officer's credibility, it should not be a concern for the appellate court. Moreover, the Supreme Court held that even if the officer's conduct was questionable, it did not rise to the level of an exceptional circumstance required for granting bail. As a result, the Supreme Court set aside the order of the Court of Appeal, cancelled the bail, and instructed the High Court to take the accused-respondent back into custody.",
    },
    {
      query:
        "Which case concerned the arrest of Jehova's witnesses who were allegedly attempting to forcibly convert citizens to their religion?",
      decision: 'sc-fr-application-no-241/2014',
      context:
        "The Supreme Court held that the arrest was unlawful and thus a violation of Article 13(1) of the Constitution because there was no reasonable reason to suspect the petitioners of having committed an offence. The court also held that there was a violation of Article 12 as the police did not produce the petitioners before a magistrate before 24 hours. The court held that there was no violation of Article 14(1)(e) because it guarantees the right to manifest religion, but the word 'practice' or 'teaching' does not encompass discussions between petitioners and members of the public during house-to-house visits, and so Article 14(1)(e) has not been violated.",
    },
    {
      query:
        'Does writ jurisdiction extend to actions/orders/proceedings of parliament or of parliamentary committees?',
      decision: 'sc-appeal-no-67/2013',
      context:
        "This case is an appeal from the judgment of the Court of Appeal to which former Chief Justice Shirani Bandaranayake, the 43rd Chief Justice of Sri Lanka, applied, seeking writs of certiorari and prohibition. She requested that the findings of the Parliamentary Select Committee, which found her guilty, be quashed and that the respondents be prohibited from taking further steps based on the report. This judgment predates her fundamental rights petition. While the writ application was still pending, Parliament debated the Select Committee's findings and subsequently voted to impeach the Chief Justice. The Court of Appeal ruled that it had the jurisdiction to exercise judicial review over findings or orders issued by persons or bodies exercising authority that affects the rights of individuals—including the impeachment process of superior court judges. The Court further held that the Parliamentary Select Committee lacked the authority to make a valid finding that adversely affected the legal rights of judges. As such, the report had no legal validity. However, the Court of Appeal did not grant the writ of prohibition because the 117 Members of Parliament who voted on the impeachment motion had not been made parties to the case, meaning the writ would not affect them. The Attorney General, who was not a party to the case but was invited to assist the court, appealed the Court of Appeal's judgment to the Supreme Court. The Supreme Court granted leave to appeal on two key questions: Did the Court of Appeal err in holding that its writ jurisdiction under Article 140 extended to parliamentary or committee proceedings? Did the Court of Appeal err in interpreting Article 140 to include Parliament or parliamentary committees within the words 'any Court of first instance or tribunal or other institution or any other person'? The Supreme Court held that Parliament and its committees are not inferior to the Court of Appeal and therefore are not subject to its writ jurisdiction, answering the second question in the negative. On the first question, the Court ruled that the writ jurisdiction of the Court of Appeal does not extend to proceedings of Parliament or its committees conducted under Article 107(2) of the Constitution.",
    },
    {
      query:
        'What is the procedure to be followed when filing an application against the judgment of a Provincial High Court?',
      decision: 'sc-la-no-111/2010',
      context:
        'Under Rule 28(3) of the Supreme Court Rules of 1990, an appellant must submit a notice of appeal in the prescribed form along with the petition of appeal. The appellant is required to provide copies of both documents for service on the respondents and themselves, as well as three additional copies. Additionally, the necessary number of stamped, addressed envelopes must be provided to ensure the notice is served on the respondents by registered post. Rule 27 further requires the petitioner to verify at the Registry that the notice has not been returned undelivered. The purpose of these rules is to ensure that all parties are properly notified and given an opportunity to be heard. Failure to comply with these requirements can result in the dismissal of applications for leave to appeal.',
    },
    {
      query:
        'Disclosure of proceedings in committee of Parliament, before they are reported to Parliament',
      decision: 'sc-fr-application-no-308/2015',
      context:
        'Disclosing committee proceedings before they are reported to Parliament is an offense punishable by either Parliament or the Supreme Court under Sections 17 and 22 of the Parliamentary Privileges Act. The Court also ruled that the actions of the Committee on Public Enterprises (COPE) are legislative, not executive or administrative, and thus do not fall under the jurisdiction of the Supreme Court as outlined in Article 126.',
    },
    {
      query: 'In what instances does acquilian action/lex acquilia apply?',
      decision: 'sc-appeal-no-130/2015',
      context:
        "Several facts were disputed in this case. The student (respondent) claimed she was denied the opportunity to sit for an exam, despite being duly registered, arriving early, sitting at the desk assigned to her exam number, and clearly demonstrating her intention to take the exam. She alleged that she was required to produce her exam timetable along with her admission card, and when she failed to do so, she was not allowed to sit for the exam. However, the appellants (invigilator and others) argued that the student was asked to wait until her assigned desk was found, but she left the hall and returned too late. According to the rules, students are not required to produce the timetable. The court drew an adverse inference against the appellants because they failed to call witnesses who could challenge the respondent's account and did not produce the attendance sheet, which should have included the respondent's name and exam number. The respondent's admission card, which was in the possession of the invigilators, clearly showed she was entitled to sit the exam, but the appellants failed to examine it. The court found the respondent's evidence more credible and the appellants' version improbable and delayed. The court ruled that the respondent had proven her case on the balance of probabilities, the standard for civil cases, and that it was the negligent conduct of the appellants that deprived her of the opportunity to sit the exam. The court also elaborated on the history of the *lex Aquilia* remedy, originating in Roman law, which provides compensation for the full pecuniary loss resulting from a defendant's wrongful actions. Three key elements of liability under *lex Aquilia* are: 1. A wrongful act or violation of a legally protected interest, 2. Fault on the part of the defendant (either intentional or preventable through reasonable care), and 3. Financial loss suffered by the plaintiff. Negligence can be through action or omission but must be actionable to invoke *lex Aquilia*, requiring negligence, a duty of care, and a breach of that duty. The court held that *lex Aquilia* remains valid in Sri Lankan law, and courts may adapt the original Roman-Dutch principles to modern contexts. Finally, the court ruled that the appellants had a duty of care to the respondent, and their breach of this duty entitled her to nominal damages for infringement of her right, exemplary damages due to the deplorable conduct of the appellants, and general damages for the psychological trauma suffered. The High Court’s award of 1 million rupees was deemed lawful and justifiable.",
    },
    {
      query: 'prevent sexual abuse of minors by law enforcement',
      decision: 'sc-fr-application-no-677/2012',
      context:
        "Police officers visited the home of a 15-year-old girl while she was alone, questioned her without her parents present, and escorted her to the police station without allowing her parents to accompany her. At the station, she was interrogated alone, detained overnight without food or water, and placed in a cell with an adult female detainee. She was also subjected to two forced sexual assault forensic examinations. The court found that these actions violated Articles 11, 12(1), and 13(1) and (2) of the Constitution. The girl's mother lodged a complaint with the Human Rights Commission (HRC), which intervened and secured her release from the hospital. The court gave guidelines to law enforcement on preventing incidents of abuse of power, particularly where children are concerned.",
    },
    {
      query: 'dissolution of parliament by president',
      decision: 'sc-fr-application-no-351/2018',
      context:
        'The Court ruled that the Presidential proclamation dissolving Parliament violates Article 70 and is therefore null and void. The Constitution expressly prohibits the President from dissolving Parliament until at least 4 years and 6 months have passed since its first meeting. In this case, the dissolution occurred only 3 years and 2 months after Parliament first convened. In reaching this decision, the Court applied several principles of interpretation: ordinary meaning of the text; harmonious interpretation of constitutional provisions; consideration of context; intent of the drafters; interpreting to enforce the rule of law rather than conferring unfettered power on public authorities; upholding constitutional integrity and supremacy; and maintaining public confidence through impartial judgment. This approach ensures a balanced and legally sound interpretation of the Constitution, free from political bias.',
    },
  ];

  setTimeout(setupTextContainers, 0);

  const { hitsData, loading } = useTypesenseSearch(query, hitsContainerId);

  // Google Analytics event to track query view
  useEffect(() => {
    if (query) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'view_query',
        event_category: 'Search Query',
        event_label: query,
      });
    }
  }, [query]);

  return (
    <MainLayout>
      {/* Set SEO Metadata and Canonical Tag */}
        <Helmet>
          <title>Performance | paralegal.lk</title>
          <meta
            name="description"
            content="How good is paralegal.lk's search performance? We conducted a benchmark study to evaluate the relevance of our search results across a range of Sri Lankan legal queries."
          />
          <link rel="canonical" href="https://www.paralegal.lk/benchmark" />
        </Helmet>

      <div className="flex flex-col justify-center mx-auto w-full max-w-6xl mt-50 p-20 text-pl-text-primary">
        <h1 className="mb-4 font-display font-semibold text-18 md:text-22">
          Evaluating{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-bold text-pl-accent"
          >
            paralegal.lk
          </a>
          's Sri Lankan case law search performance
        </h1>
        <p className="leading-relaxed mb-2">
          We conducted a benchmark study evaluating the relevance of our search
          results across a range of queries on Sri Lankan law. In this blog post, we detail
          the methodology, summarise the key findings, and list the actual
          queries used for the study and their live search results.
        </p>

        <div className=" overflow-hidden w-fit flex flex-col bg-pl-bg-elevated border border-pl-border rounded-lg text-14 text-left">
          <a
            href="#search-modes"
            className="p-10 px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
          >
            Search modes
          </a>
          <hr className="border-t border-pl-border" />
          <a
            href="#methodology"
            className="p-10 px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
          >
            Evaluation methodology
          </a>
          <hr className="border-t border-pl-border" />
          <a
            href="#findings"
            className="p-10 px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
          >
            Key findings
          </a>
          <hr className="border-t border-pl-border" />
          <a
            href="#sample-queries"
            className="p-10 px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
          >
            Sample study queries and live results
          </a>
          <hr className="border-t border-pl-border" />
          <a
            href="#failure-queries"
            className="p-10 px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
          >
            Failure queries
          </a>
          <hr className="border-t border-pl-border" />
          <a
            href="#conclusion"
            className="p-10 px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
          >
            Conclusion and future Work
          </a>
          <hr className="border-t border-pl-border" />
          <a
            href="#development-plan"
            className="p-10 px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
          >
            paralegal.lk development plan
          </a>
          <hr className="border-t border-pl-border" />
          <a
            href="#addendum"
            className="p-10 px-3 py-2 text-left hover:text-pl-accent-hover hover:bg-pl-bg-subtle"
          >
            Addendum: even more sample queries
          </a>
        </div>

        <h2
          id="search-modes"
          className="md:mb-1 mt-4 md:mt-6 font-semibold text-16 md:text-18 pl-benchmark-sections-axs"
        >
          paralegal.lk search modes
        </h2>
        <p className="leading-relaxed mb-2">
          Before going further, it is instructive to understand the different
          search modes available on{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>
          .
        </p>
        <ul>
          <li>
            <strong className="font-semibold">Exact Search:</strong> This mode
            matches queries word-for-word against court decisions.
            <p className="leading-relaxed mb-2">
              This mode is very — although, not exactly — similar to Ctrl+F
              search on documents that we use regularly. Except, in this case,
              you are doing such a matching against the whole collection of
              court decisions as opposed to a single document.
            </p>
          </li>
          <li>
            <strong className="font-semibold">AI Search:</strong> This mode
            matches queries against decisions at the semantic level.
            <p className="leading-relaxed mb-2">
              For instance, under this mode, you might expect the query "Can an
              employer fire an employee without notice in Sri Lanka?" to match
              with a decision that contains the sentence "Under Sri Lankan
              labour law, dismissing an employee requires giving adequate notice
              unless there is just cause for immediate termination."
            </p>
            <p className="leading-relaxed mb-2">
              Though the words "fire" and "without notice" do not appear in the
              decision, the semantic match happens because "fire" is related to
              "dismiss" and "without notice" is captured by "requires giving
              adequate notice." Under this mode, we match the meaning of the
              query not the literal words.
            </p>
          </li>
          <li>
            <strong className="font-semibold">Hybrid (Default) Search:</strong>{' '}
            This is the default search mode on{' '}
            <a
              href="https://www.paralegal.lk"
              className="font-semibold text-pl-accent"
            >
              paralegal.lk
            </a>{' '}
            and combines Exact and AI modes to give the user the best of both
            worlds.
          </li>
        </ul>

        <p className="leading-relaxed mb-2">
          In addition to the above search modes,{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>{' '}
          also offers a specialised search mode called{' '}
          <strong className="font-semibold">Case Number Search</strong> which
          allows users to find specific decisions via their case number. But
          this mode is irrelevant for the benchmark study.
        </p>

        <h2
          id="methodology"
          className="md:mb-1 mt-4 md:mt-6 font-semibold text-16 md:text-18 pl-benchmark-sections-axs"
        >
          Evaluation methodology
        </h2>
        <p className="leading-relaxed mb-2">
          We collaborated with a lawyer who provided us with a total of 47 legal
          queries and the most relevant court decision for each query. The only
          constraint on the lawyer was that the most relevant decision should be
          already indexed in our database. This is necessary since{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>{' '}
          presently only covers decisions from the Supreme Court and the Court
          of Appeal from the year 2009 onwards. Otherwise, the lawyer was free
          to choose any query they deemed fit. One might expect a great search
          engine to return the most relevant decision as the top result for
          every query.
        </p>
        <p className="leading-relaxed mb-2">
          The benchmarking process involved the following steps:
        </p>
        <ul>
          <li>
            <strong className="font-semibold">Query selection:</strong> The
            lawyer formulated 47 questions covering various legal topics,
            reflecting different levels of complexity.
          </li>
          <li>
            <strong className="font-semibold">Search execution:</strong> Each
            question was searched using Default and AI Search modes.
          </li>
          <li>
            <strong className="font-semibold">Results evaluation:</strong> For
            each query, we obtained the rank of the most relevant decision in
            the list of results returned by{' '}
            <a
              href="https://www.paralegal.lk"
              className="font-semibold text-pl-accent"
            >
              paralegal.lk
            </a>
            .
          </li>
        </ul>

        <h2
          id="findings"
          className="md:mb-1 mt-4 md:mt-6 font-semibold text-16 md:text-18 pl-benchmark-sections-axs"
        >
          Key findings
        </h2>
        <p className="leading-relaxed mb-2">
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>{' '}
          demonstrated strong baseline performance, proving that we have a
          strong foundation upon which to build even better search experiences
          for our users. Specifically, Default mode returned the most relevant
          decision within Top 5 for 72.5% of the queries and within Top 10 for
          78.8% of the queries.
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>
          , under Default mode, returned the most relevant decision as the first
          result (Top 1) for 42.5% of the queries.
        </p>

        <h3 className="my-2 font-medium">
          Table 1: Performance evaluation study summary
        </h3>
        <div className="w-full bg-pl-bg-elevated  border border-pl-border rounded-xl text-14 text-left">
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-pl-border">
                <th className="p-10">Rank</th>
                <th className="p-10">Search mode</th>
                <th className="p-10">Number of queries</th>
                <th className="p-10">Percentage (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-pl-bg-subtle">
                <td className="py-5 px-10">Top 1</td>
                <td className="py-5 px-10">Default Search</td>
                <td className="py-5 px-10">20</td>
                <td className="py-5 px-10">42.6%</td>
              </tr>
              <tr className="hover:bg-pl-bg-subtle">
                <td className="py-5 px-10">Top 2-5</td>
                <td className="py-5 px-10">Default Search</td>
                <td className="py-5 px-10">14</td>
                <td className="py-5 px-10">29.9%</td>
              </tr>
              <tr className="hover:bg-pl-bg-subtle">
                <td className="py-5 px-10">Top 6-10</td>
                <td className="py-5 px-10">Default Search</td>
                <td className="py-5 px-10">3</td>
                <td className="py-5 px-10">6.3%</td>
              </tr>
              <tr className="hover:bg-pl-bg-subtle">
                <td className="py-5 px-10">Top 5</td>
                <td className="py-5 px-10">AI Search</td>
                <td className="py-5 px-10">7</td>
                <td className="py-5 px-10">14.9%</td>
              </tr>
              <tr className="hover:bg-pl-bg-subtle">
                <td className="py-5 px-10">Top 6-10</td>
                <td className="py-5 px-10">AI Search</td>
                <td className="py-5 px-10">1</td>
                <td className="py-5 px-10">2.1%</td>
              </tr>
              <tr className="hover:bg-pl-bg-subtle">
                <td className="py-5 px-10">Outside Top 10</td>
                <td className="py-5 px-10">Default Search and AI Search</td>
                <td className="py-5 px-10">2</td>
                <td className="py-5 px-10">4.2%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2
          id="sample-queries"
          className="md:mb-1 mt-4 md:mt-6 font-semibold text-16 md:text-18 pl-benchmark-sections-axs"
        >
          Try for yourself: sample study queries and live results
        </h2>
        <p className="leading-relaxed mb-2">
          Below are some of the queries used in the benchmark study along with
          the most relevant decision for each query. Please note that some of
          the queries are intentionally verbose to add complexity. You can click
          on the "View live Top #3 results from{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>{' '}
          search engine" under a query to see the Top 3 results from{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>{' '}
          for that query, in-line. Alternatively, you can click on the query
          which will re-direct you to the main search engine page to see the
          full results for the query. We provide additional context, including
          an overview of the most relevant decision, for those interested in the
          legal background.
        </p>

        <div className="sample-queries">
          <ul>
            {sampleQueries.map((item, index) => (
              <li key={index} className="mt-6">
                <a
                  href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=${item.query}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong className="font-normal hover:underline text-pl-accent">
                    {item.query}
                  </strong>
                </a>
                <div className="content">
                  <p className="decision">
                    Most relevant decision:{' '}
                    <span className="font-semibold">{item.decision}</span>
                  </p>
                  <p
                    ref={(el) => (textRefs.current[index] = el)}
                    className={`text-pl-text-secondary ${
                      expanded[index] ? 'expanded' : ''
                    }`}
                    onClick={() => handleExpandToggle(index)}
                  >
                    <span className="font-semibold">Context: </span>
                    {item.context}
                  </p>
                  <span
                    className="read-more-toggle"
                    onClick={() => handleExpandToggle(index)}
                  ></span>
                </div>
                <details>
                  <summary
                    className="text-13 text-pl-text-tertiary cursor-pointer"
                    onClick={(e) =>
                      handleDetailsClick(
                        item.query,
                        index,
                        e.target.closest('details')
                      )
                    }
                  >
                    View live Top #3 results from paralegal.lk search engine
                  </summary>
                  <div className="bg-pl-bg-elevated rounded-lg border border-pl-border">
                    {loading ? (
                      <p className="leading-relaxed px-2 text-12 text-pl-text-tertiary mb-2">
                        Loading...
                      </p>
                    ) : (
                      <div id={`hits${index + 1}`} className="pb-20">
                        {hitsData.length > 0 ? (
                          hitsData.map((hit, idx) => (
                            <Hit key={idx} hit={hit} />
                          ))
                        ) : (
                          <div>No results found.</div>
                        )}
                      </div>
                    )}
                  </div>
                </details>
              </li>
            ))}
          </ul>
        </div>

        <h2
          id="failure-queries"
          className="md:mb-1 mt-4 md:mt-6 font-semibold text-16 md:text-18 pl-benchmark-sections-axs"
        >
          Failure queries
        </h2>
        <p className="leading-relaxed mb-2">
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>{' '}
          failed to return the most relevant decision within the Top 10 results
          under both Default and AI modes for two queries. We list them below
          and discuss why they might have proved challenging to the engine.
        </p>
        <div className="sample-queries">
          <ul>
            <li>
              <strong className="font-normal text-pl-accent">
                Have trade unions instituted FR applications in the SC?
              </strong>
              <p className="leading-relaxed mb-2">
                The most relevant decision to this question is{' '}
                <span className="font-semibold">sc-fr-15/2015</span>, in which
                the Supreme Court held that Article 126 does not empower
                unincorporated bodies such as trade unions to file fundamental
                rights applications.
              </p>
              <p className="leading-relaxed mb-2">
                The query, phrased exactly as above, returned the relevant
                decision at rank 11 under AI mode and a much lower rank under
                Default mode. However, when we rephrased the query to{' '}
                <strong className="font-normal text-pl-accent">
                  Have trade unions instituted fundamental rights applications
                  in the Supreme Court?
                </strong>
                , the correct decision was ranked 2nd under AI mode and 6th
                under Default mode.
              </p>
            </li>
            <li>
              <strong className="font-normal text-pl-accent">
                Who can appeal a judgment in a criminal case?
              </strong>
              <p className="leading-relaxed mb-2">
                The most relevant decision to this question is{' '}
                <span className="font-semibold">sc-spl-la-145/2014</span>, in
                which the Supreme Court refused an application for special leave
                to appeal and directed the Court of Appeal (CA) to hear the
                parties and decide whether to grant leave to appeal. The CA had
                to determine whether the petitioners had locus standi to appeal
                the judgment.
              </p>
              <p className="leading-relaxed mb-2">
                This is the only query where{' '}
                <a
                  href="https://www.paralegal.lk"
                  className="font-semibold text-pl-accent"
                >
                  paralegal.lk
                </a>{' '}
                failed to return the most relevant decision within the Top 10
                results under both Default and AI modes, even with rephrasing.
                An initial hypothesis is that the query consists of words that
                are too generic in a collection of court decisions.
              </p>
            </li>
          </ul>
        </div>

        <h2
          id="conclusion"
          className="md:mb-1 mt-4 md:mt-6 font-semibold text-16 md:text-18 pl-benchmark-sections-axs"
        >
          Conclusion and future work
        </h2>
        <p className="leading-relaxed mb-2">
          The benchmark study provided valuable insights into the performance of{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>
          's case law search performance. While Default mode demonstrated strong
          baseline performance, there were instances where AI only mode was
          necessary to return the most relevant decision within the Top 5 or Top
          10 results. In some instances, rephrasing the query &mdash; as in the
          case of 'FR' to 'fundamental rights', for the trade union related
          question &mdash; significantly improved the ranking of the most
          relevant decision. In the next weeks, we plan to evolve the{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>{' '}
          to a place where it self-selects both the mode and the query
          rephrasing strategy. In our next evaluation, we will compare our
          peformance against LawLanka's and also evaluate single word or short
          phrase queries &mdash; such as, "Prasanna Jayawardena" to list all the
          the decisions made by Justice Prasanna Jayawardena &mdash; which were
          not included in this study.
        </p>

        <h2
          id="development-plan"
          className="md:mb-1 mt-4 md:mt-6 font-semibold text-16 md:text-18 pl-benchmark-sections-axs"
        >
          <a href="https://www.paralegal.lk">paralegal.lk</a> development plan{' '}
        </h2>

        <p className="leading-relaxed mb-2">
          We are in the process of adding NLRs, SLLRs, and Special
          Determinations to the{' '}
          <a
            href="https://www.paralegal.lk"
            className="font-semibold text-pl-accent"
          >
            paralegal.lk
          </a>{' '}
          database. Do expect a public announcement in the second week of
          October 2024. Thank you for your support.
        </p>

        <h2
          id="addendum"
          className="md:mb-1 mt-4 md:mt-6 font-semibold text-16 md:text-18 pl-benchmark-sections-axs"
        >
          Addendum: even more sample study queries
        </h2>
        <div className="queries">
          <ul>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Can%20appeal%20an%20ex%20parte%20judgment%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Who can appeal an ex parte judgment?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 1)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Which%20case%20dealt%20with%20the%20arrest%20and%20assault%20of%20university%20students%20involved%20in%20a%20Satyagraha%20%28peaceful%20protest%29%20held%20in%20front%20of%20the%20UGC%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Which case dealt with the arrest and assault of university
                students involved in a Satyagraha (peaceful protest) held in
                front of the UGC?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 1)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=What%20are%20the%20remedies%20available%20to%20a%20shareholders%20under%20the%20Companies%20Act%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                What are the remedies available to a shareholders under the
                Companies Act?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 1)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Bribery%20conviction%20appeal%20case%20identification%20parade%20uncertified%20reports%20dock%20identification`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Bribery conviction appeal case identification parade uncertified
                reports dock identification
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 1)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Which%20case%20concerned%20the%20pollution%20of%20groundwater%20in%20Chunnakam%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Which case concerned the pollution of groundwater in Chunnakam?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 1)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=What%20must%20a%20workman%20prove%20to%20establish%20constructive%20termination%2C%20and%20how%20does%20the%20principle%20of%20%27comply%20and%20complain%27%20apply%20in%20cases%20involving%20transfer%20orders%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                What must a workman prove to establish constructive termination,
                and how does the principle of 'comply and complain' apply in
                cases involving transfer orders?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 2)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Does%20the%20duty%20to%20give%20reasons%20for%20a%20verdict%20extend%20to%20Court%20Martial%20proceedings%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Does the duty to give reasons for a verdict extend to Court
                Martial proceedings?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 2)
              </div>
            </li>
            <li>
              <a
                href="What is the best proof of tenancy?"
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                What is the best proof of tenancy?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 2)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Under%20what%20circumstances%20can%20an%20Attorney%20at%20Law%20be%20suspended%20from%20practice%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Under what circumstances can an Attorney at Law be suspended
                from practice?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 3)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=What%20determines%20the%20subject%20matter%20jurisdiction%20of%20the%20Commercial%20High%20Court%20%28CHC%29%20in%20cases%20involving%20commercial%20transactions`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                What determines the subject matter jurisdiction of the
                Commercial High Court (CHC) in cases involving commercial
                transactions
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 3)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Is%20there%20a%20fixed%20minimum%20price%20at%20which%20tea%20factories%20should%20purchase%20green%20leaf%20tea%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Is there a fixed minimum price at which tea factories should
                purchase green leaf tea?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 3)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Can%20the%20state%20requisition%20private%20property%20for%20use%20by%20Cooperative%20Societies%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Can the state requisition private property for use by
                Cooperative Societies?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 3)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=Who%20is%20a%20lawful%20lessee%3F%20`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                Who is a lawful lessee?{' '}
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 3)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=On%20what%20grounds%20can%20divorce%20be%20obtained%20in%20Sri%20Lanka%3F`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                On what grounds can divorce be obtained in Sri Lanka?
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 3)
              </div>
            </li>
            <li>
              <a
                href={`${import.meta.env.VITE_MAIN_DOMAIN_URL}/search?paralegal%5Bquery%5D=time%20bar%20decree%20for%20possession%20resistance%20obstruction%20hindering%20ousting%20calendar%20month%20interpretation`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-normal hover:underline text-pl-accent"
              >
                time bar decree for possession resistance obstruction hindering
                ousting calendar month interpretation
              </a>
              <div className="text-14 mb-2">
                (most relevant decision rank: 3)
              </div>
            </li>
          </ul>
        </div>
      </div>
      <ScrollToTopButton />
    </MainLayout>
  );
};

export default Benchmark;
